import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Button,
} from '../ui';
import { Loader2 } from 'lucide-react';
import type { ArtifactSummary } from '../../hooks/use-artifact-store';

export interface DeleteConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Title of the conversation being deleted, for the prompt. */
  title: string;
  /**
   * Artifact count and size, or null when it could not be established.
   *
   * Null is NOT "none" — see the copy below. A listing failure that displayed
   * "0 files" would invite the user to delete a conversation believing it had
   * no artifacts, and quietly take real ones with it.
   */
  summary: ArtifactSummary | null;
  /** True while the summary is still being fetched. */
  loadingSummary: boolean;
  /** Confirm. `deleteArtifacts` reflects the checkbox. */
  onConfirm: (opts: { deleteArtifacts: boolean }) => void;
  deleting?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Confirm deleting a conversation, and ask about its artifacts separately.
 *
 * Two decisions are encoded here, both deliberate:
 *
 * **The checkbox is unchecked by default.** A mis-click must never destroy
 * generated work. This accepts that orphaned artifacts accumulate when users
 * delete conversations without ticking it — the right trade, because orphans are
 * a storage-cost problem with a sweep as its remedy, whereas a destroyed image
 * the user wanted is unrecoverable.
 *
 * **The count is shown before the choice.** "Also delete artifacts?" with no
 * number is a question nobody can answer responsibly.
 */
export function DeleteConversationDialog({
  open,
  onOpenChange,
  title,
  summary,
  loadingSummary,
  onConfirm,
  deleting = false,
}: DeleteConversationDialogProps) {
  const [deleteArtifacts, setDeleteArtifacts] = useState(false);

  // Reset on each open: a checkbox that remembered "yes" from last time would
  // make the safe default a lie.
  useEffect(() => {
    if (open) setDeleteArtifacts(false);
  }, [open]);

  const hasArtifacts = (summary?.count ?? 0) > 0;
  const unknownArtifacts = !loadingSummary && summary === null;

  return (
    <Dialog open={open} onOpenChange={(next) => !deleting && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete conversation</DialogTitle>
          <DialogDescription>
            This permanently deletes &ldquo;{title}&rdquo;. It cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {loadingSummary && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Checking for saved files…
          </p>
        )}

        {hasArtifacts && (
          <div className="space-y-2">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={deleteArtifacts}
                onChange={(e) => setDeleteArtifacts(e.target.checked)}
                disabled={deleting}
              />
              <span>
                Also delete{' '}
                <strong>
                  {summary!.count} saved file{summary!.count === 1 ? '' : 's'}
                </strong>{' '}
                ({formatBytes(summary!.bytes)}) — attachments and generated images
                from this conversation.
              </span>
            </label>
            {deleteArtifacts && (
              // Stated only when it is about to happen, so the warning lands
              // where it matters rather than being permanent furniture.
              <p className="pl-6 text-xs text-muted-foreground">
                Files are removed one at a time, so this is not atomic — if some
                cannot be deleted, the rest still will be.
              </p>
            )}
          </div>
        )}

        {unknownArtifacts && (
          // Never render this as "0 files". The user is told the truth: we do
          // not know, and anything saved will be left alone.
          <p className="text-sm text-muted-foreground">
            Could not check for saved files. Any attachments or generated images
            from this conversation will be kept.
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={deleting || loadingSummary}
            onClick={() => onConfirm({ deleteArtifacts })}
          >
            {deleting
              ? 'Deleting…'
              : deleteArtifacts
                ? 'Delete conversation and files'
                : 'Delete conversation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
